import is from '@sindresorhus/is';

import type {
    AlignmentRole,
    AlignmentTask,
    Capabilities,
    TaskCharacter,
} from '../../../data-contract/position-alignment';
import { taskCharacterSchema } from '../../../data-contract/position-alignment';
import type { TaskManagementTaskDetail } from '../../../data-contract/task-management';
import { pickLocalizedText } from '../../../position-management';
import { isAnswered } from './practice/practice-model';

export const TASK_BANDS: readonly TaskCharacter[] = taskCharacterSchema.options;

export type TaskSummary = {
    readonly described: number;
    readonly removed: number;
    readonly total: number;
};

/** Tasks without a kind of work read as routine, as on the position's work screen. */
export const taskBand = (task: Pick<AlignmentTask, 'character'>): TaskCharacter => task.character ?? 'ROUTINE';

export const orderedTasks = (tasks: readonly AlignmentTask[]): readonly AlignmentTask[] =>
    TASK_BANDS.flatMap((band) =>
        tasks.filter((task) => taskBand(task) === band).toSorted((first, second) => first.order - second.order),
    );

/** The employee withdraws any task added in the alignment, the manager only their own. */
export const canWithdrawTask = (task: Pick<AlignmentTask, 'added_by' | 'origin'>, viewer: AlignmentRole): boolean =>
    task.origin === 'ALIGNMENT' && (viewer === 'EMPLOYEE' || (viewer === 'MANAGER' && task.added_by === 'MANAGER'));

/** A backend without `describe_tasks` lets only those who edit the tasks describe them. */
export const canDescribeTasks = ({ describe_tasks, edit_tasks }: Capabilities): boolean => describe_tasks ?? edit_tasks;

/** Whether the backend still generates the content of a task added by its title. */
export const isGeneratingTask = (task: Pick<AlignmentTask, 'generation_state'>): boolean =>
    task.generation_state === 'QUEUED' || task.generation_state === 'STARTED';

/** The work task's content is still being written in the background. */
export const isGeneratingTaskDetail = ({
    generationState,
}: Pick<TaskManagementTaskDetail, 'generationState'>): boolean =>
    generationState === 'queued' || generationState === 'started';

/** `stepCount` is the task's documented step count; unknown counts conservatively, as if it had steps. */
export const taskDescribed = (task: Pick<AlignmentTask, 'practice' | 'removed'>, stepCount?: number): boolean =>
    !task.removed && isAnswered(task.practice, stepCount);

export const taskSummary = (
    tasks: readonly AlignmentTask[],
    stepCounts: ReadonlyMap<string, number> = new Map(),
): TaskSummary => {
    const kept = tasks.filter((task) => !task.removed);

    return {
        described: kept.filter((task) => taskDescribed(task, stepCounts.get(task.id))).length,
        removed: tasks.filter((task) => task.removed).length,
        total: kept.length,
    };
};

export const initialTaskId = (tasks: readonly AlignmentTask[]): string | null =>
    tasks.find((task) => !task.removed && !taskDescribed(task))?.id ?? tasks[0]?.id ?? null;

export const addedTaskId = (before: readonly AlignmentTask[], after: readonly AlignmentTask[]): string | null => {
    const known = new Set(before.map(({ id }) => id));

    return after.find(({ id }) => !known.has(id))?.id ?? null;
};

export const taskTitle = (task: Pick<AlignmentTask, 'titles'>, languageTag: string, sourceLanguage: string): string =>
    pickLocalizedText(task.titles, languageTag, sourceLanguage) ?? '';

export const taskDescription = (
    task: Pick<AlignmentTask, 'description' | 'translations'>,
    languageTag: string,
    sourceLanguage: string,
): string | null => {
    if (is.nonEmptyStringAndNotWhitespace(task.description)) return task.description;

    return pickLocalizedText(task.translations, languageTag, sourceLanguage) ?? null;
};
